// ============================================================================
// Availability queue — every OMS order still waiting on vendor lead times,
// grouped by enquiry.
//
// This is the OMS successor to the legacy "Estimate Item Details" desk. A line
// lands here when placement couldn't source it: the estimate's lead time is a
// cart-time snapshot that expires at order_placed, so Hold Stock orders,
// overnight payments and anything crossing IST midnight arrive unsourced.
//
// Why it matters: an unconfirmed line is SILENT. The order is placed and the
// customer is waiting, but nothing is allocated, no PO exists, and no error is
// raised anywhere. This queue is the only place that wait is visible — so rows
// are oldest-first and age is coloured.
//
// Why it is grouped by enquiry, not listed by line: the planner merges demand
// into one allocation — and so one purchase order — per (vendor, warehouse,
// lead time), but only within a single confirm request. A line-by-line desk
// therefore raised a separate PO for every line even when the whole enquiry
// came from one supplier on one day. The unit of work here is the enquiry: open
// it, fill in what is known for all of its lines, submit once. Lines nobody can
// answer yet are left blank and simply stay on the desk.
// ============================================================================
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useCallback, useEffect, useState } from "react";
import CopyText from "../components/copy_text";
import ExpressTag from "../components/express_tag";
import ProductThumb from "../components/product_thumb";
import { fmtQty } from "../format";
import { VendorInventoryRows } from "../../../components/inventory/vendor_inventory_rows";
import { listPendingAvailabilityGrouped } from "../oms.api";
import { PendingAvailabilityGroup, PendingAvailabilityLine } from "../types";
import BatchConfirmModal from "./components/batch_confirm_modal";

dayjs.extend(relativeTime);

// Sum of every group column width below — `scroll.x` must match it exactly or the right-pinned
// Action column overlaps its neighbour.
const TABLE_WIDTH = 180 + 180 + 120 + 120 + 130 + 190;

// Enquiries per page. Pagination is by ORDER, so a lead is never split across pages — which is
// what would push an operator back into confirming it in pieces.
const PAGE_SIZE = 25;

// How long the oldest line of an enquiry has been unsourced. A day-old enquiry means a placed
// order nobody has started procuring — that earns colour, because nothing else in the system will
// raise its hand.
const AMBER_HOURS = 6;
const RED_HOURS = 24;

const ageHours = (createdAt?: string | null): number | null =>
  createdAt ? dayjs().diff(dayjs(createdAt), "hour") : null;

// The enquiry has been waiting as long as its oldest line has.
const oldestCreatedAt = (g: PendingAvailabilityGroup): string | null =>
  g.lines.reduce<string | null>(
    (oldest, l) => (!oldest || (l.created_at && l.created_at < oldest) ? l.created_at ?? oldest : oldest),
    null
  );

const dash = (v?: string | null) => v || <span style={{ color: "#bbb" }}>—</span>;

// Express enquiries are the ones whose lead time is the promise — so they get the same marker here
// as everywhere else in the OMS. Both spellings are accepted because the payloads differ by screen.
const isExpress = (g: PendingAvailabilityGroup): boolean =>
  g.is_express === true || g.mode === "express";

interface Props {
  // Keeps a parent badge (sidebar / tab) in sync without it having to fetch separately. Counts
  // LINES, as before — the badge answers "how much work is outstanding", not "how many orders".
  onCountChange?: (count: number) => void;
}

const AvailabilityQueue: React.FC<Props> = ({ onCountChange }) => {
  const [groups, setGroups] = useState<PendingAvailabilityGroup[]>([]);
  const [orderCount, setOrderCount] = useState(0);
  const [lineCount, setLineCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState(""); // the term actually queried
  // Server-side: lines whose variant is / is not on the sellable (auto-fill) list.
  const [autoFill, setAutoFill] = useState<"all" | "allowed" | "blocked">("all");
  const [loading, setLoading] = useState(false);
  // The enquiry being filled in. Its whole pending line set goes into the dialog at once.
  const [confirming, setConfirming] = useState<PendingAvailabilityLine[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listPendingAvailabilityGrouped({
      search: applied || undefined,
      limit: PAGE_SIZE,
      offset,
      ...(autoFill !== "all" ? { auto_fill: autoFill } : {}),
    });
    setLoading(false);
    if (res) {
      setGroups(res.results);
      setOrderCount(res.count);
      setLineCount(res.line_count);
      onCountChange?.(res.line_count);
    }
  }, [applied, offset, autoFill, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const runSearch = () => {
    setOffset(0); // a new term must start at page 1, or an empty page looks like "no results"
    setApplied(search.trim());
  };

  // --- the lines of one enquiry, shown inline when it is expanded -------------
  const lineColumns: ColumnsType<PendingAvailabilityLine> = [
    {
      title: "",
      key: "image",
      width: 56,
      render: (_, r) => <ProductThumb src={r.product?.image_url} />,
    },
    {
      title: "Product",
      key: "product",
      render: (_, r) => (
        <div>
          <div>{r.product?.product_name || "—"}</div>
          {r.product?.private_label_name && (
            <div style={{ fontSize: 12, color: "#531dab" }}>
              Private label: {r.product.private_label_name}
            </div>
          )}
          {r.product?.sku && (
            <div style={{ fontSize: 11, color: "#999" }}>SKU {r.product.sku}</div>
          )}
        </div>
      ),
    },
    {
      // For a partially-sourced line, the qty to work is what's LEFT — show that, with the ordered
      // total as context, so the desk sees "6 of 10" rather than the full 10.
      title: "Qty to source",
      key: "qty",
      width: 130,
      render: (_, r) => {
        const allocated = Number(r.allocated_qty ?? 0);
        if (allocated <= 0) return fmtQty(r.ordered_qty);
        return (
          <div>
            <div>{fmtQty(r.remaining_qty)}</div>
            <div style={{ fontSize: 11, color: "#999" }}>of {fmtQty(r.ordered_qty)}</div>
          </div>
        );
      },
    },
    {
      title: "Seeded source",
      key: "source",
      width: 220,
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div>{dash(r.source_wh_name)}</div>
          {r.source_vendor_name && (
            <div style={{ color: "#888" }}>{r.source_vendor_name}</div>
          )}
        </div>
      ),
    },
    {
      title: "Waiting",
      dataIndex: "created_at",
      width: 130,
      render: (v) => (v ? dayjs(v).fromNow(true) : "—"),
    },
  ];

  // --- the enquiries ---------------------------------------------------------
  const columns: ColumnsType<PendingAvailabilityGroup> = [
    {
      title: "Enquiry",
      key: "lead",
      width: 180,
      render: (_, g) => (
        <div>
          <div>{dash(g.lead_id)}</div>
          {isExpress(g) && (
            <div style={{ margin: "2px 0" }}>
              <ExpressTag show />
            </div>
          )}
          <CopyText value={String(g.sales_order_id)} label="SO number">
            <span style={{ fontSize: 12, color: "#1677ff" }}>SO #{g.sales_order_id}</span>
          </CopyText>
        </div>
      ),
    },
    { title: "Client", dataIndex: "client_name", width: 180, render: dash },
    { title: "Deliver to", dataIndex: "ship_pincode", width: 120, render: dash },
    {
      title: "To source",
      key: "pending",
      width: 120,
      render: (_, g) => (
        <Tag color={g.pending_line_count > 1 ? "blue" : "default"}>
          {g.pending_line_count} line{g.pending_line_count === 1 ? "" : "s"}
        </Tag>
      ),
    },
    {
      title: "Waiting",
      key: "waiting",
      width: 130,
      render: (_, g) => {
        const v = oldestCreatedAt(g);
        if (!v) return "—";
        const hrs = ageHours(v) ?? 0;
        const color = hrs >= RED_HOURS ? "#cf1322" : hrs >= AMBER_HOURS ? "#d48806" : undefined;
        return (
          <Tooltip title={dayjs(v).format("DD MMM YYYY HH:mm")}>
            <span style={{ color, fontWeight: color ? 600 : undefined }}>
              {dayjs(v).fromNow(true)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 190,
      fixed: "right",
      render: (_, g) => (
        <Button type="primary" size="small" onClick={() => setConfirming(g.lines)}>
          Fill availability ({g.pending_line_count})
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          placeholder="Enquiry id, product or client phone"
          prefix={<SearchOutlined />}
          style={{ width: 300 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={runSearch}
          onBlur={runSearch}
        />
        <Select
          value={autoFill}
          style={{ width: 190 }}
          onChange={(v) => {
            setOffset(0);
            setAutoFill(v);
          }}
          options={[
            { value: "all", label: "Auto-fill: all products" },
            { value: "allowed", label: "Auto-fill allowed" },
            { value: "blocked", label: "Auto-fill off" },
          ]}
        />
        <Tooltip title="Refresh">
          <Button icon={<ReloadOutlined />} onClick={load} />
        </Tooltip>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {orderCount} enquir{orderCount === 1 ? "y" : "ies"} · {lineCount} line
          {lineCount === 1 ? "" : "s"} awaiting a lead time · oldest first
        </Typography.Text>
      </Space>

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
        Fill an enquiry in one go — lines sharing a supplier and lead time then become a single
        purchase order. Anything you don't know yet can be left blank; it stays on this desk.
      </Typography.Paragraph>

      <Table
        rowKey="sales_order_id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={groups}
        scroll={{ x: TABLE_WIDTH }}
        expandable={{
          expandedRowRender: (g) => (
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              columns={lineColumns}
              dataSource={g.lines}
              expandable={{
                // EVERY line is expandable here, unlike the procurement cart.
                // Gating on "has stock" hid the panel on 91% of this desk —
                // exactly the lines that are waiting BECAUSE we hold nothing —
                // and an absent caret reads as a broken feature rather than as
                // an answer. `showEmpty` makes "none anywhere" explicit.
                expandedRowRender: (r) => (
                  <VendorInventoryRows
                    rows={r.vendor_inventory ?? []}
                    showEmpty
                    variantId={r.variant_id}
                    onAutoFillChange={load}
                  />
                ),
              }}
            />
          ),
        }}
        onRow={(g) => {
          const hrs = ageHours(oldestCreatedAt(g)) ?? 0;
          return {
            style: {
              background: hrs >= RED_HOURS ? "#fff1f0" : hrs >= AMBER_HOURS ? "#fffbe6" : undefined,
            },
          };
        }}
        pagination={{
          current: Math.floor(offset / PAGE_SIZE) + 1,
          pageSize: PAGE_SIZE,
          total: orderCount,
          showSizeChanger: false,
          onChange: (page) => setOffset((page - 1) * PAGE_SIZE),
        }}
        locale={{ emptyText: "Every placed order is sourced 🎉" }}
      />

      <BatchConfirmModal
        open={!!confirming.length}
        lines={confirming}
        onClose={() => setConfirming([])}
        onConfirmed={load}
      />
    </div>
  );
};

export default AvailabilityQueue;
